import { motion } from 'framer-motion'
import { Link } from '@inertiajs/react'
import { ArrowUpRight, Terminal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { NetworkAnimation } from './NetworkAnimation'
import { fadeInUp, fadeIn, staggerContainer } from '@/lib/motion'

export function HeroSection() {
  return (
    <section
      aria-label="Hero"
      className="aurora-bloom relative isolate min-h-[92vh] overflow-hidden pt-20"
    >
      {/* Background composition — grid + soft engineered fade */}
      <div className="absolute inset-0 engineer-grid engineer-grid-fade opacity-50" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 pb-24 pt-12 md:gap-16 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:px-8 lg:pt-20">
        {/* ───── Left: copy ───── */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="relative z-10 max-w-2xl"
        >
          {/* Eyebrow */}
          <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-md border border-foreground/10 bg-background/60 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground backdrop-blur">
              <span className="live-dot" />
              public beta
            </span>
            <span className="hidden font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70 sm:inline">
              TLS 1.3 · DTLS-SRTP · HMAC webhooks
            </span>
          </motion.div>

          {/* Display headline — keeps the original positioning copy */}
          <motion.h1
            variants={fadeInUp}
            className="mt-7 font-heading text-[clamp(2.4rem,5.6vw,4.5rem)] font-bold leading-[1] tracking-tight text-foreground"
          >
            Build Realtime Communication
            <br />
            <span className="signal-text">Into Any App</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl"
          >
            Voice, video, and screen sharing APIs powered by a global edge network.
            Sub-100ms latency. Simple REST API. Ship in minutes, not months.
          </motion.p>

          {/* CTAs */}
          <motion.div variants={fadeInUp} className="mt-9 flex flex-wrap items-center gap-3">
            <Button size="lg" asChild className="group h-12 rounded-lg px-6 text-sm font-semibold">
              <Link href="/sign_up">
                Start Building Free
                <ArrowUpRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="h-12 rounded-lg px-5 text-sm font-medium"
            >
              <Link href="/docs">
                <Terminal className="mr-2 h-4 w-4" />
                View API Docs
              </Link>
            </Button>
            <Link
              href="/test"
              className="ml-1 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
            >
              try.live ↗
            </Link>
          </motion.div>

          {/* Spec strip — dev-trust signals */}
          <motion.dl
            variants={fadeInUp}
            className="mt-12 grid max-w-xl grid-cols-3 gap-x-6 gap-y-1"
          >
            {SPECS.map((s) => (
              <div key={s.label} className="flex flex-col">
                <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80">
                  {s.label}
                </dt>
                <dd className="font-heading text-3xl leading-none tracking-tight">
                  {s.value}
                  {s.unit && (
                    <span className="ml-0.5 align-baseline font-heading text-sm text-muted-foreground">
                      {s.unit}
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </motion.dl>
        </motion.div>

        {/* ───── Right: signal visual ───── */}
        <motion.div
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="relative z-10"
        >
          <div className="relative">
            <NetworkAnimation />
          </div>
        </motion.div>
      </div>

      {/* Morse-code signal strip — encodes a brand message + a decoder
          discount code visitors can quote when signing up. */}
      <MorseStrip />
    </section>
  )
}

function MorseStrip() {
  return (
    <div className="relative z-10 mx-auto mt-6 max-w-7xl space-y-3 px-6 lg:px-8">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground/70">
          <span className="live-dot" />
          live · 7.2 mhz · konex.actual
        </div>
      </div>

      {/* Scrolling Morse code — dots + dashes only, repeated for seamless loop.
          The message is decoded only by visitors who recognize and translate
          the international Morse alphabet — there's no surface-level hint.
          What they decode IS the reward. */}
      <div
        className="relative overflow-hidden"
        style={{
          maskImage:
            'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
        }}
        aria-label="Morse code transmission"
      >
        <div
          className="marquee-track flex w-max whitespace-nowrap font-mono text-lg leading-none sm:text-xl"
          aria-hidden
        >
          {[0, 1].map((cycle) => (
            <span
              key={cycle}
              className="signal-text px-10 tracking-[0.4em] opacity-80"
            >
              {MORSE_TICKER}
            </span>
          ))}
        </div>
      </div>

      {/* Soft hint — anyone who decodes the marquee can email us. We don't
          tell them what they'll find, what to send, or what they get. The
          mailto stays neutral so the surprise isn't in the URL preview. */}
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground/60">
        got the signal?{' '}
        <a
          href="mailto:hello@konexzero.com?subject=Got%20the%20signal"
          className="text-foreground/80 underline underline-offset-4 transition-colors hover:text-[hsl(var(--brand-1))]"
        >
          hello@konexzero.com
        </a>
      </p>
    </div>
  )
}

// Verified specs only:
//   - < 100 ms — sub-100ms latency claim already on the existing About page
//   - 300+ POPs — same source ("global edge network spanning 300+ cities")
//   - −75% — calculated from Dashboard::UsageController.OUR_COST_PER_1K_MIN ($1.00)
//     vs. AGORA_COST_PER_1K_MIN ($3.99): (3.99 − 1) / 3.99 ≈ 0.75
const SPECS = [
  { label: 'glass-to-glass', value: '< 100', unit: 'ms' },
  { label: 'edge POPs',      value: '300',   unit: '+' },
  { label: 'vs. agora',      value: '−75',   unit: '%' },
]

// Morse-code marquee — encodes a brand message in international Morse so the
// hero ends with a "live signal" stripe of dots and dashes instead of a fake
// trusted-by logo wall. Each letter is space-separated, words are " / ".
const MORSE_TABLE: Record<string, string> = {
  A: '.-',    B: '-...',  C: '-.-.',  D: '-..',   E: '.',     F: '..-.',
  G: '--.',   H: '....',  I: '..',    J: '.---',  K: '-.-',   L: '.-..',
  M: '--',    N: '-.',    O: '---',   P: '.--.',  Q: '--.-',  R: '.-.',
  S: '...',   T: '-',     U: '..-',   V: '...-',  W: '.--',   X: '-..-',
  Y: '-.--',  Z: '--..',
  '0': '-----',  '1': '.----',  '2': '..---',  '3': '...--',  '4': '....-',
  '5': '.....',  '6': '-....',  '7': '--...',  '8': '---..',  '9': '----.',
}

// Word separator: international Morse uses a longer audio gap (7 dot-units
// of silence) between words and has no character for it. The de-facto
// transcription convention is "/" — but "/" is itself a valid Morse char
// (-..-., used in callsigns), which is mildly ambiguous. We're decorative
// so we use "|" instead: visually unambiguous, distinct ink-weight from the
// dots and dashes, never confused with an actual letter.
function toMorse(text: string): string {
  return text
    .toUpperCase()
    .split(/\s+/)
    .map((word) =>
      word
        .split('')
        .map((ch) => MORSE_TABLE[ch] || '')
        .filter(Boolean)
        .join(' '),
    )
    .filter(Boolean)
    .join(' | ')
}

// Discount code visitors can mail in. Tied to the signal/morse theme of the
// hero strip — easy to remember if you noticed the dots and dashes.
const DISCOUNT_CODE = 'SIGNAL25'

// Plain-text source — the morse marquee renders only its Morse form. Note the
// discount code is encoded *inside* the message: anyone who actually decodes
// the dots and dashes gets the coupon as a reward, layered on top of the soft
// "got the signal? hello@…" hint below. The trailing digits 2 and 5
// (..---  .....) make a distinctive five-dot stamp at the end of every loop.
const MORSE_MESSAGE = `KONEXZERO REAL TIME DISCOUNT CODE ${DISCOUNT_CODE}`
const MORSE_TICKER = toMorse(MORSE_MESSAGE)
